import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Badge, Spinner, 
  Alert, Modal, Form, InputGroup 
} from 'react-bootstrap';

import { 
  FaEdit, FaTrash, FaPlus, FaSearch, 
  FaUserTie, FaPhone, FaEnvelope, FaIdCard, FaHistory,
  FaCar, FaCarSide, FaCalendarAlt, FaPalette, FaTag, FaCamera, FaTrashAlt
} from 'react-icons/fa';

import axios from 'axios';
import { Link } from 'react-router-dom';

const DriverList = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);


  const getVehicleTypeBadge = (vehicleType) => {
    const variants = {
      Sedan: 'primary',
      SUV: 'info',
      Van: 'warning',
      Minibus: 'success'
    };
    return <Badge bg={variants[vehicleType]}>{vehicleType}</Badge>;
  };


  
  const fetchDrivers = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/drivers`, {
        headers: { 'x-auth-token': token },
        params: { 
          page,
          search: searchTerm 
        }
      });
      
      setDrivers(response.data.drivers);
      setTotalPages(response.data.totalPages);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching drivers:', err);
      setError(err.response?.data?.error || 'Failed to load drivers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, [searchTerm]);

  const handleDelete = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      await axios.delete(`${process.env.REACT_APP_API_URL}/admin/drivers/${driverToDelete._id}`, {
        headers: { 'x-auth-token': token }
      });
      
      setShowDeleteModal(false);
      fetchDrivers(currentPage);
    } catch (err) {
      console.error('Error deleting driver:', err);
      setError(err.response?.data?.error || 'Failed to delete driver');
    }
  };

  const getAvailabilityBadge = (isAvailable) => {
    return isAvailable ? (
      <Badge bg="success">Available</Badge>
    ) : (
      <Badge bg="danger">Unavailable</Badge>
    );
  };

  return (
    <div className="drivers-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Driver Management</h2>
        <Link to="/admin/drivers/add" className="btn btn-primary">
          <FaPlus className="me-2" />
          Add New Driver
        </Link>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="mb-3">
        <InputGroup>
          <InputGroup.Text>
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search drivers by name, email or phone"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
      </div>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>License</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
            {drivers.length > 0 ? (
    drivers.map((driver) => (
      <tr key={driver._id}>
        <td>
          <div className="d-flex align-items-center">
            <FaUserTie className="me-2" />
            <div>
              <div>{driver.name}</div>
              <small className="text-muted">{driver.email}</small>
            </div>
          </div>
        </td>
        <td>
          <div>
            <div><FaPhone className="me-2" /> {driver.phone}</div>
            <div><FaEnvelope className="me-2" /> {driver.email}</div>
          </div>
        </td>
        <td>
          <div>
            <FaIdCard className="me-2" />
            {driver.licenseNumber}
          </div>
          <div className="mt-1">
            <FaCar className="me-2" />
            {driver.vehiclePlateNumber}
          </div>
          <div className="mt-1">
            {getVehicleTypeBadge(driver.vehicleType)}
          </div>
        </td>
        <td>{getAvailabilityBadge(driver.isAvailable)}</td>
        <td>
          <div className="d-flex gap-2">
            <Link 
              to={`/admin/drivers/edit/${driver._id}`}
              className="btn btn-sm btn-primary"
            >
              <FaEdit />
            </Link>
            <Link
              to={`/admin/drivers/jobs/${driver._id}`}
              className="btn btn-sm btn-info"
            >
              <FaHistory />
            </Link>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                setDriverToDelete(driver);
                setShowDeleteModal(true);
              }}
            >
              <FaTrash />
            </Button>
          </div>
        </td>
      </tr>
    ))
  ) : (
    <tr>
      <td colSpan="5" className="text-center">
        No drivers found
      </td>
    </tr>
  )}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <nav>
                <ul className="pagination">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => fetchDrivers(currentPage - 1)}
                    >
                      Previous
                    </button>
                  </li>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <li 
                      key={page} 
                      className={`page-item ${currentPage === page ? 'active' : ''}`}
                    >
                      <button 
                        className="page-link" 
                        onClick={() => fetchDrivers(page)}
                      >
                        {page}
                      </button>
                    </li>
                  ))}
                  
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => fetchDrivers(currentPage + 1)}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete driver <strong>{driverToDelete?.name}</strong>? 
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete Driver
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DriverList;